import React, { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { employmentTypes,  benefitOptions, payTypeOptions, currencyOptions, frequencyOptions } from '../../data/postJobFields';
import { url } from "../../data/url";

function PostJob() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [newSkill, setNewSkill] = useState("");
  const [errors, setErrors] = useState({});
  const quillRef = useRef(null);

  const [formData, setFormData] = useState({
    jobTitle: "",
    jobLocation: "",
    streetAddress: "",
    remote: "",
    employmentType: "",
    jobDescription: "",
    department: "",
    companyPitch: "",
    benefits: [],
    payType: "range",
    minPay: "",
    maxPay: "",
    exactPay: "",
    currency: "USD",
    frequency: "annually",
    plusCommission: false,
    activeFrom: "",
    activeTill: "",
    skills: [],
  });

  const resetForm = () => {
    setFormData({
      jobTitle: "",
      jobLocation: "",
      streetAddress: "",
      remote: "",
      employmentType: "",
      jobDescription: "",
      department: "",
      companyPitch: "",
      benefits: [],
      payType: "range",
      minPay: "",
      maxPay: "",
      exactPay: "",
      currency: "USD",
      frequency: "annually",
      plusCommission: false,
      activeFrom: "",
      activeTill: "",
      skills: [],
    });
    setErrors({});
  };

  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        const response = await axios.get(`${url.path}/employer/check-auth`, {
          withCredentials: true
        });
        if (response.status !== 200) {
          throw new Error('Not authorized');
        }
      } catch (error) {
        navigate('/login');
        console.error("Authorization check failed:", error);
      }
    };

    checkAuthorization();
  }, [navigate]);

  useEffect(() => {
    if (id) {
      const fetchJobData = async () => {
        try {          
          const response = await axios.get(`${url.path}/employer/edit-job/${id}`, {
            withCredentials: true
          });
          if (response.status === 200) {
            const jobData = response.data.job;
            setFormData({
              ...jobData,
              activeFrom: jobData.activeFrom ? new Date(jobData.activeFrom).toISOString().split('T')[0] : '',
              activeTill: jobData.activeTill ? new Date(jobData.activeTill).toISOString().split('T')[0] : '',
            });
          }
        } catch (error) {
          if(error.response && error.response.status === 401) {
            navigate('/login');
          }
          console.error("Error fetching job data:", error);
          showToast("Error fetching job data. Please try again.", "error");
          // navigate('/dashboard'); // Redirect to dashboard or appropriate page
        }
      };
  
      fetchJobData();
    }
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuillChange = (field) => (content) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: content,
    }));
  };

  const handleCompanyPitchChange = useCallback((content) => {
    const unprivilegedEditor = quillRef.current.getEditor();
    const text = unprivilegedEditor.getText().trim();
    const length = text.length;

    if (length <= 180) {
      setFormData((prevData) => ({
        ...prevData,
        companyPitch: content,
      }));
    } else {
      // If the content exceeds 180 characters, revert to the previous state
      const quill = quillRef.current.getEditor();
      quill.history.undo();
    }
  }, []);
  const remainingChars = 180 - (formData.companyPitch.replace(/<[^>]+>/g, '').trim().length);

  const addSkill = () => {
    const trimmedSkill = newSkill.trim().toLowerCase();
    if (trimmedSkill !== "" && !formData.skills.includes(trimmedSkill)) {
      setFormData((prevData) => ({
        ...prevData,
        skills: [...prevData.skills, trimmedSkill],
      }));
      setNewSkill("");
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData((prevData) => ({
      ...prevData,
      skills: prevData.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.jobTitle) newErrors.jobTitle = "Job title is required";
    if (!formData.jobLocation) newErrors.jobLocation = "Job location is required";
    if (!formData.remote) newErrors.remote = "Remote work option is required";
    if (!formData.employmentType) newErrors.employmentType = "Employment type is required";
    if (!formData.jobDescription) newErrors.jobDescription = "Job description is required";
    
    // Validate pay information
  if (formData.payType === "range") {
    if ((formData.maxPay && !formData.minPay) || (formData.minPay && !formData.maxPay)) {
      newErrors.pay = "Both minimum and maximum pay are required for pay range";
    } else if (Number(formData.minPay) > 0 && Number(formData.minPay) >= Number(formData.maxPay)) {
      newErrors.pay = "Minimum pay must be less than maximum pay";
    }
  }

  // Validate time duration
  if (formData.activeFrom && formData.activeTill) {
    const fromDate = new Date(formData.activeFrom);
    const tillDate = new Date(formData.activeTill);
    if (fromDate > tillDate) {
      newErrors.timeDuration = "Active From date must be before or equal to Active Till date";
    }
  }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showToast = (message, type = "success") => {
    toast[type](message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: `custom-toast custom-toast-${type}`,
      bodyClassName: "custom-toast-body",
      progressClassName: "custom-toast-progress",
    });
  };

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const handleSubmit = async (e, status) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Strip HTML tags from jobDescription and companyPitch
      const checkJobDescription = stripHtmlTags(formData.jobDescription).trim();

      // Check if the cleaned fields are empty
      if (!checkJobDescription) {
        showToast("Job description cannot be empty", "error");
        return;
      }

      const cleanedFormData = {
        ...formData,
        companyPitch: stripHtmlTags(formData.companyPitch).trim(),
        status
      };

      if (cleanedFormData.companyPitch === "") {
        delete cleanedFormData.companyPitch; // Remove companyPitch if it's empty
      }

      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true  
      };

      let response;
      if (id) {
        // Update existing job
        response = await axios.put(`${url.path}/employer/post-job/${id}`, cleanedFormData, config);
      } else {
        // Create new job
        response = await axios.post(`${url.path}/employer/post-job`, cleanedFormData, config);
      }
      
      if (response.status === 200 || response.status === 201) {
        showToast(id ? "Job updated successfully!" : (status === "active" ? "Job posted successfully!" : "Job saved as draft!"));
        if(status === "active" || id)
        {
          resetForm();
        }
        if(id)
        {
          navigate(`/employer/dashboard`);
        }
          // You might want to redirect the user or show a success message here
        } else {
          // Handle server-side validation errors or other issues
          showToast("Some error occurred while submitting the form.", "error");
        }
      
      } catch (error) {
        const errorMessage = error.response?.data?.message || "An error occurred while posting the job";
        
        showToast(errorMessage, "error");
      }
    } else {
      const requiredFields = ['jobTitle', 'jobLocation', 'remote', 'employmentType', 'jobDescription'];
      for (const field of requiredFields) {
        if (!formData[field]) {
          let data;
          if(field === 'jobTitle') {
            data = 'Job title';
          }
          else if(field === 'jobLocation') {
            data = 'Job location';
          }
          else if(field === 'remote') {
            data = 'Remote work';
          }
          else if(field === 'employmentType') {
            data = 'Employment type';
          }
          else {
            data = 'Job description';
          }
          showToast(`${data} cannot be empty`, "error");
          return;
        }
      }
      showToast("Please fix the errors in the form before submitting.", "error");
    }
  };

  return (
    <div className="post-job">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="page-title-container">
      <h1 className="page-title">
        <FontAwesomeIcon icon={faPlus} className="icon" />
        {id ? "Edit Internship" : "Post an Internship"}
      </h1>
      </div>
      <form className="job-form" onSubmit={(e) => handleSubmit(e, "active")}>
        <div className="form-group">
          <label htmlFor="jobTitle">*Job Title:</label>
          <input
            type="text"
            id="jobTitle"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleInputChange}
            placeholder="Enter job title"
          />
          {errors.jobTitle && <span className="error">{errors.jobTitle}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="jobLocation">*Job Location:</label>
          <input
            type="text"
            id="jobLocation"
            name="jobLocation"
            value={formData.jobLocation}
            onChange={handleInputChange}
            placeholder="Enter ZIP or city, state"
          />
          {errors.jobLocation && <span className="error">{errors.jobLocation}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="streetAddress">Street Address:</label>
          <input 
            type="text" 
            id="streetAddress" 
            name="streetAddress"
            value={formData.streetAddress}
            onChange={handleInputChange}
            placeholder="123 Main Street" 
          />
        </div>

        <div className="form-group">
          <label>*Can this role be performed as a "remote work from home" job with no on-site work required?</label>
          <div className="radio-group">
            {["Yes", "No"].map((option) => (
              <label key={option}>
                <input
                  type="radio"
                  name="remote"
                  value={option.toLowerCase()}
                  checked={formData.remote === option.toLowerCase()}
                  onChange={handleInputChange}
                />
                {option}
              </label>
            ))}
          </div>
          {errors.remote && <span className="error">{errors.remote}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="employmentType">*Employment Type:</label>
          <select
            id="employmentType"
            name="employmentType"
            value={formData.employmentType}
            onChange={handleInputChange}
          >
            <option value="">- Select employment type -</option>
            {employmentTypes.map((type) => (
              <option key={type} value={type.toLowerCase()}>
                {type}
              </option>
            ))}
          </select>
          {errors.employmentType && <span className="error">{errors.employmentType}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="department">Department:</label>
          <input
            type="text"
            id="department"
            name="department"
            value={formData.department}
            onChange={handleInputChange}
            placeholder="Eg. Sales, Marketing, Tech"
          />
        </div>

        <div className="form-group">
          <label htmlFor="jobDescription">*Job Details:</label>
          <p className="helper-text">
            Add all things about the job responsibilities, minimum qualification needed, preferred qualifications or any other extra information here.
          </p>
          <ReactQuill
            theme="snow"
            value={formData.jobDescription}
            onChange={handleQuillChange("jobDescription")}
          />
          {errors.jobDescription && <span className="error">{errors.jobDescription}</span>}
        </div>

        <div className="form-group">
      <label htmlFor="companyPitch">Why Work at This Company?</label>
      <p className="helper-text">
        Give a one-line sales pitch for working at this company (180 characters max).
        Note: editing this field will affect all jobs at this hiring company.
      </p>
      <ReactQuill 
        ref={quillRef}
        theme="snow"
        value={formData.companyPitch}
        onChange={handleCompanyPitchChange}
        modules={{
          toolbar: [['bold', 'italic', 'underline']],
        }}
      />
      <p className="character-count" style={{ color: remainingChars === 0 ? 'red' : 'inherit' }}>
        {remainingChars} characters remaining
      </p>
    </div>

        <div className="form-group">
          <label>Benefits:</label>
          <div className="info-section">
            <div className="benefits-group">
              {benefitOptions.map((benefit) => (
                <label key={benefit}>
                  <input
                    type="checkbox"
                    name="benefits"
                    value={benefit.toLowerCase().replace(/\s+/g, '_')}
                    checked={formData.benefits.includes(benefit.toLowerCase().replace(/\s+/g, '_'))}
                    onChange={(e) => {
                      const { checked, value } = e.target;
                      setFormData((prevData) => ({
                        ...prevData,
                        benefits: checked
                          ? [...prevData.benefits, value]
                          : prevData.benefits.filter((b) => b !== value),
                      }));
                    }}
                  />
                  {benefit}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Pay Information:</label>
          <p className="helper-text">
            Please include pay information. Job postings with pay information
            receive maximum visibility!
          </p>
          <div className="info-section">
            <div className="pay-info-fields">
              <div className="pay-field">
                <label htmlFor="payType">Pay Type</label>
                <select
                  id="payType"
                  name="payType"
                  value={formData.payType}
                  onChange={handleInputChange}
                >
                  {payTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "range" ? "Pay Range" : "Exact Pay Amount"}
                    </option>
                  ))}
                </select>
              </div>

              {formData.payType === "range" ? (
                <>
                  <div className="pay-field">
                    <label htmlFor="minPay">Minimum Pay</label>
                    <input 
                      type="number" 
                      id="minPay" 
                      name="minPay"
                      value={formData.minPay}
                      onChange={handleInputChange}
                      placeholder="$ Min" 
                    />
                  </div>
                  <div className="pay-field">
                    <label htmlFor="maxPay">Maximum Pay</label>
                    <input 
                      type="number" 
                      id="maxPay" 
                      name="maxPay"
                      value={formData.maxPay}
                      onChange={handleInputChange}
                      placeholder="$ Max" 
                    />
                  </div>
                </>
              ) : (
                <div className="pay-field">
                  <label htmlFor="exactPay">Exact Pay</label>
                  <input 
                    type="number" 
                    id="exactPay" 
                    name="exactPay"
                    value={formData.exactPay}
                    onChange={handleInputChange}
                    placeholder="$ Pay" 
                  />
                </div>
              )}

              <div className="pay-field">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  {currencyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pay-field">
                <label htmlFor="frequency">Frequency</label>
                <select
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                >
                  {frequencyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="commission-checkbox">
              <input 
                type="checkbox" 
                id="plusCommission" 
                name="plusCommission"
                checked={formData.plusCommission}
                onChange={handleInputChange}
              />
              <label htmlFor="plusCommission">Plus Commission</label>
            </div>
          </div>
          {errors.pay && <span className="error">{errors.pay}</span>}
        </div>

        <div className="form-group">
          <label>Time Duration:</label>
          <div className="time-duration">
            <div className="time-duration-field">
              <p className="helper-text">Active From</p>
              <input 
                type="date" 
                id="activeFrom" 
                name="activeFrom"
                value={formData.activeFrom}
                onChange={handleInputChange}
              />
            </div>
            <div className="time-duration-field">
              <p className="helper-text">Active Till</p>
              <input 
                type="date" 
                id="activeTill" 
                name="activeTill"
                value={formData.activeTill}
                onChange={handleInputChange}
              />
            </div>
          </div>
          {errors.timeDuration && <span className="error">{errors.timeDuration}</span>}
        </div>

        <div className="form-group">
          <label>Skills:</label>
          <p className="helper-text">
            Target the exact job seekers you need by adding skill keywords
            below.
          </p>
          <div className="skill-input">
            <input
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              placeholder="Enter a desired skill"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addSkill();
                }
              }}
            />
            <button type="button" onClick={addSkill}>
              Add
            </button>
          </div>
          <div className="skill-tags">
            {formData.skills.map((skill, index) => (
              <span key={index} className="skill-tag">
                {skill}
                <button
                  type="button"
                  onClick={() => removeSkill(skill)}
                  className="remove-skill"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="button-group">
        <button type="submit" className="submit-button">
          {id ? "Update Job" : "Continue to post"}
        </button>
          <button type="button" className="draft-button" onClick={(e) => handleSubmit(e, "draft")}>
            Save as Draft
          </button>
        </div>
      </form>
    </div>
  );
}

export default PostJob;